import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { AuthContext, type User } from "./auth-context";

async function fetchMe(): Promise<User | null> {
	try {
		return await api.get<User>("/auth/me");
	} catch (err) {
		if (err instanceof Error && err.message === "Unauthorized") {
			return null;
		}
		throw err;
	}
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const queryClient = useQueryClient();

	const {
		data: user,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: ["auth", "me"],
		queryFn: fetchMe,
		retry: false,
	});

	const logoutMutation = useMutation({
		mutationFn: () => api.post("/auth/logout", {}),
		onSuccess: () => {
			queryClient.setQueryData(["auth", "me"], null);
			window.location.href = "/login";
		},
	});

	const logout = useCallback(() => {
		logoutMutation.mutate();
	}, [logoutMutation]);

	const stableRefetch = useCallback(() => {
		void refetch();
	}, [refetch]);

	const value = useMemo(
		() => ({
			user: user ?? null,
			isLoading,
			isAuthenticated: !!user,
			logout,
			refetch: stableRefetch,
		}),
		[user, isLoading, logout, stableRefetch],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
